import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, fmtError } from "@/lib/api";
import { fmtDate, L } from "@/constants/labels";
import { Send, RefreshCw, PlayCircle, Search, Mail, ListChecks, Users2, BarChart3 } from "lucide-react";

/**
 * Supervision des notifications e-mail (admin) :
 *  - Envois : journal `email_logs` avec filtres type / statut / recherche
 *  - File : items `notification_queue` en attente de traitement par le scheduler
 *  - Matches : paires (besoin, prestataire) déjà notifiées (idempotence)
 *  - Campagne : force manuellement l'enfilage des matches d'un besoin
 *  - Stats : compteurs globaux
 */
const TYPE_LABEL = {
  password_reset: "Réinit. mot de passe",
  verify_email: "Vérification e-mail",
  candidature: "Candidature reçue",
  match_besoin: L.wording.adminNotifAlerteChantiers,
  match_prestataire: L.wording.adminNotifAlerteArtisans,
};

const STATUS_BADGE = {
  sent: "badge-valid",
  failed: "badge-danger",
  skipped: "badge-warning",
};

export default function AdminNotifications() {
  const [subtab, setSubtab] = useState("logs");
  const [logs, setLogs] = useState([]);
  const [queue, setQueue] = useState([]);
  const [matches, setMatches] = useState([]);
  const [stats, setStats] = useState(null);
  const [filterType, setFilterType] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [q, setQ] = useState("");
  const [besoinIdForMatch, setBesoinIdForMatch] = useState("");
  const [besoinIdForTrigger, setBesoinIdForTrigger] = useState("");
  const [busy, setBusy] = useState(false);

  const loadLogs = async () => {
    try {
      const params = {};
      if (filterType) params.type = filterType;
      if (filterStatus) params.status_filter = filterStatus;
      if (q) params.q = q;
      const r = await api.get("/admin/notifications/logs", { params });
      setLogs(r.data);
    } catch (err) { toast.error(fmtError(err)); }
  };

  const loadQueue = async () => {
    try {
      const r = await api.get("/admin/notifications/queue");
      setQueue(r.data);
    } catch (err) { toast.error(fmtError(err)); }
  };

  const loadStats = async () => {
    try {
      const r = await api.get("/admin/notifications/stats");
      setStats(r.data);
    } catch (err) { toast.error(fmtError(err)); }
  };

  const loadMatches = async () => {
    if (!besoinIdForMatch) { setMatches([]); return; }
    try {
      const r = await api.get("/admin/notifications/matches-sent", {
        params: { besoin_id: besoinIdForMatch },
      });
      setMatches(r.data);
    } catch (err) { toast.error(fmtError(err)); }
  };

  useEffect(() => {
    if (subtab === "logs") loadLogs();
    if (subtab === "queue") loadQueue();
    if (subtab === "stats") loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subtab]);

  const triggerCampaign = async () => {
    const bid = besoinIdForTrigger.trim();
    if (!bid) { toast.error("Renseignez l'ID du besoin"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/admin/notifications/trigger/${bid}`);
      toast.success(`${r.data.enqueued} prestataire(s) enfilé(s) — matches déjà envoyés ignorés`);
      if (subtab === "queue") await loadQueue();
    } catch (err) { toast.error(fmtError(err)); }
    finally { setBusy(false); }
  };

  const runSchedulerTick = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/notifications/scheduler-tick");
      toast.success(`Tick exécuté — ${r.data.processed_users} utilisateur(s) traité(s)`);
      if (subtab === "logs") await loadLogs();
      if (subtab === "queue") await loadQueue();
    } catch (err) { toast.error(fmtError(err)); }
    finally { setBusy(false); }
  };

  const SubTab = ({ id, label, icon: Icon }) => (
    <button
      onClick={() => setSubtab(id)}
      data-testid={`notif-subtab-${id}`}
      className={`px-3 py-1.5 text-xs font-semibold rounded-full border flex items-center gap-1 ${
        subtab === id ? "bg-[#1B3A5C] text-white border-[#1B3A5C]" : "bg-white text-[#3E4C59] border-[#CBD5E1]"
      }`}
    >
      <Icon size={14} /> {label}
    </button>
  );

  return (
    <div className="mt-4">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <SubTab id="logs" label="Envois" icon={Mail} />
        <SubTab id="queue" label="File d'attente" icon={ListChecks} />
        <SubTab id="matches" label={L.wording.adminNotifCorrespondances} icon={Users2} />
        <SubTab id="campaign" label="Campagne manuelle" icon={PlayCircle} />
        <SubTab id="stats" label="Stats" icon={BarChart3} />
        <div className="ml-auto">
          <button
            onClick={runSchedulerTick}
            disabled={busy}
            data-testid="notif-scheduler-tick"
            className="btn-outline text-xs flex items-center gap-1"
          >
            <RefreshCw size={14} /> Forcer un tick scheduler
          </button>
        </div>
      </div>

      {/* Envois */}
      {subtab === "logs" && (
        <div>
          <div className="card p-3 flex flex-wrap items-end gap-2 mb-3">
            <div>
              <label className="block text-xs text-[#3E4C59]">Type</label>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                data-testid="notif-filter-type"
                className="border border-[#CBD5E1] rounded px-2 py-1 text-sm"
              >
                <option value="">Tous</option>
                {Object.entries(TYPE_LABEL).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-[#3E4C59]">Statut</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                data-testid="notif-filter-status"
                className="border border-[#CBD5E1] rounded px-2 py-1 text-sm"
              >
                <option value="">Tous</option>
                <option value="sent">Envoyé</option>
                <option value="failed">Échec</option>
                <option value="skipped">Ignoré</option>
              </select>
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs text-[#3E4C59]">Recherche e-mail</label>
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && loadLogs()}
                data-testid="notif-filter-q"
                placeholder="contact@…"
                className="border border-[#CBD5E1] rounded px-2 py-1 text-sm w-full"
              />
            </div>
            <button
              onClick={loadLogs}
              data-testid="notif-filter-apply"
              className="btn-primary text-xs flex items-center gap-1"
            >
              <Search size={14} /> Filtrer
            </button>
          </div>

          <div className="card overflow-x-auto">
            <table className="table-compact w-full">
              <thead>
                <tr>
                  <th>Date</th><th>Destinataire</th><th>Type</th><th>Statut</th>
                  <th>Nb agrégé</th><th>Provider</th><th>Message ID</th><th>Erreur</th>
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 && (
                  <tr><td colSpan={8} className="text-center text-[#3E4C59]">Aucun envoi</td></tr>
                )}
                {logs.map((l) => (
                  <tr key={l.id} data-testid={`notif-log-row-${l.id}`}>
                    <td className="whitespace-nowrap">{fmtDate(l.sent_at)}</td>
                    <td>{l.recipient_email}</td>
                    <td>{TYPE_LABEL[l.type] || l.type}</td>
                    <td>
                      <span className={STATUS_BADGE[l.status] || "text-xs"}>
                        {l.status === "sent" ? "Envoyé" : l.status === "failed" ? "Échec" : l.status === "skipped" ? "Ignoré" : l.status}
                      </span>
                    </td>
                    <td className="text-center">{l.aggregated_count || 1}</td>
                    <td className="text-xs text-[#3E4C59]">{l.provider || "—"}</td>
                    <td className="font-mono text-xs">{l.provider_id ? String(l.provider_id).slice(0, 24) : "—"}</td>
                    <td className="text-xs text-[#C0392B] max-w-[240px] truncate" title={l.error || ""}>{l.error || ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* File d'attente */}
      {subtab === "queue" && (
        <div className="card overflow-x-auto">
          <table className="table-compact w-full">
            <thead>
              <tr>
                <th>Créé</th><th>Utilisateur</th><th>Type</th><th>Besoin</th><th>Prestataire</th><th>Statut</th>
              </tr>
            </thead>
            <tbody>
              {queue.length === 0 && (
                <tr><td colSpan={6} className="text-center text-[#3E4C59]">File vide</td></tr>
              )}
              {queue.map((it) => (
                <tr key={it.id} data-testid={`notif-queue-row-${it.id}`}>
                  <td className="whitespace-nowrap">{fmtDate(it.created_at)}</td>
                  <td className="font-mono text-xs">{it.user_id}</td>
                  <td>{TYPE_LABEL[it.kind] || it.kind}</td>
                  <td className="font-mono text-xs">{it.besoin_id}</td>
                  <td className="font-mono text-xs">{it.prestataire_id}</td>
                  <td>
                    <span className={STATUS_BADGE[it.status] || "text-xs"}>{it.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Matches par besoin */}
      {subtab === "matches" && (
        <div>
          <div className="card p-3 flex items-end gap-2 mb-3">
            <div className="flex-1">
              <label className="block text-xs text-[#3E4C59]">ID du besoin</label>
              <input
                type="text"
                value={besoinIdForMatch}
                onChange={(e) => setBesoinIdForMatch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && loadMatches()}
                data-testid="notif-matches-besoin-id"
                placeholder="ex: 6820a3f8…"
                className="border border-[#CBD5E1] rounded px-2 py-1 text-sm w-full font-mono"
              />
            </div>
            <button
              onClick={loadMatches}
              data-testid="notif-matches-load"
              className="btn-primary text-xs flex items-center gap-1"
            >
              <Search size={14} /> Charger
            </button>
          </div>
          <div className="card overflow-x-auto">
            <table className="table-compact w-full">
              <thead><tr><th>Prestataire ID</th><th>Envoyé le</th></tr></thead>
              <tbody>
                {matches.length === 0 && (
                  <tr><td colSpan={2} className="text-center text-[#3E4C59]">
                    {besoinIdForMatch ? "Aucun match enregistré pour ce besoin" : "Saisissez un ID de besoin"}
                  </td></tr>
                )}
                {matches.map((m) => (
                  <tr key={m.id} data-testid={`notif-match-row-${m.id}`}>
                    <td className="font-mono text-xs">{m.prestataire_id}</td>
                    <td>{fmtDate(m.sent_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Campagne manuelle */}
      {subtab === "campaign" && (
        <div className="card p-4 max-w-2xl">
          <h3 className="text-base font-semibold text-[#1B3A5C]">Déclencher une campagne manuellement</h3>
          <p className="text-sm text-[#3E4C59] mt-1">
            Cherche à nouveau tous les prestataires qui matchent ce besoin et enfile un envoi pour chacun.
            Les prestataires déjà notifiés pour ce besoin (table <code>notification_matches_sent</code>) sont ignorés.
            L'envoi effectif se fait au prochain tick du scheduler (toutes les 5 min) — ou cliquez « Forcer un tick » ci-dessus.
          </p>
          <div className="mt-3">
            <label className="block text-xs text-[#3E4C59]">ID du besoin</label>
            <input
              type="text"
              value={besoinIdForTrigger}
              onChange={(e) => setBesoinIdForTrigger(e.target.value)}
              data-testid="notif-trigger-besoin-id"
              placeholder="ex: 6820a3f8…"
              className="border border-[#CBD5E1] rounded px-2 py-1 text-sm w-full font-mono"
            />
          </div>
          <button
            onClick={triggerCampaign}
            disabled={busy || !besoinIdForTrigger.trim()}
            data-testid="notif-trigger-run"
            className="btn-primary mt-3 flex items-center gap-1"
          >
            <Send size={14} /> Enfiler les matches
          </button>
        </div>
      )}

      {/* Stats */}
      {subtab === "stats" && stats && (
        <div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.entries({
              "Envois 24h": stats.sent_24h,
              "Envois 7j": stats.sent_7d,
              "Échecs 7j": stats.failed_7d,
              "File active": stats.queue_pending,
              "Matches uniques": stats.matches_sent_total,
              "Users désabonnés": stats.unsubscribed_users,
            }).map(([k, v]) => (
              <div key={k} className="card p-4">
                <div className="text-xs uppercase text-[#3E4C59]">{k}</div>
                <div className="text-3xl font-bold text-[#1B3A5C]">{v ?? 0}</div>
              </div>
            ))}
          </div>
          <div className="card p-4 mt-3">
            <div className="text-xs uppercase text-[#3E4C59] mb-2">Répartition par type (30 j)</div>
            <table className="table-compact w-full">
              <thead><tr><th>Type</th><th>Envoyés</th><th>Échecs</th><th>Ignorés</th></tr></thead>
              <tbody>
                {(stats.by_type || []).map((row) => (
                  <tr key={row.type}>
                    <td>{TYPE_LABEL[row.type] || row.type}</td>
                    <td>{row.sent}</td>
                    <td>{row.failed}</td>
                    <td>{row.skipped}</td>
                  </tr>
                ))}
                {(!stats.by_type || stats.by_type.length === 0) && (
                  <tr><td colSpan={4} className="text-center text-[#3E4C59]">Aucune donnée</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
